import { useDashboardPathname as usePathname } from "@/hooks/useDashboardNavigation";

/**
 * First URL segment for routes that are not `/[org]/…` org-scoped pages.
 * If we treated these as org names (e.g. `/codesmith-autofix-configuration` →
 * org "codesmith-autofix-configuration"), prefetch and
 * metrics APIs would hit the wrong path and error.
 *
 * When adding a new top-level route, include its path segment here.
 */
const RESERVED_TOP_LEVEL_SEGMENTS = new Set([
  "admin",
  "analytics",
  "autofix-settings",
  "cache",
  "cli",
  "codesmith-autofix-configuration",
  "copy-prompt",
  "docker-analytics",
  "enable-autofix",
  "extension",
  "github-actions-analytics",
  "logs",
  "machine-definitions",
  "monitors",
  "rightsize",
  "runs",
  "sessions",
  "settings",
  "stickydisks",
  "support",
  "testboxes",
  "usage",
  "welcome",
]);

/** Org-less pages under a segment that stays a valid org name elsewhere, e.g. orgs `codesmith` and `proof`. */
const ORG_LESS_PATHS = new Set(["codesmith/proof", "proof"]);

export function useOrgFromPathname(): string | undefined {
  const pathname = usePathname();
  if (!pathname) return undefined;
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  if (!first) return undefined;
  if (
    RESERVED_TOP_LEVEL_SEGMENTS.has(first.toLowerCase()) ||
    ORG_LESS_PATHS.has(segments.join("/").toLowerCase())
  ) {
    return undefined;
  }
  return first;
}
